/**
 * 이상한 방 - 걸기 규칙 (판돈 올리기)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 4.3절(탄환 경제) · 4.5절(점수) · 5절(3번 - 말싸움을 규칙으로)
 *            `이상한방-상태모델-v0.1.md` 12절(속도 보너스를 밀리초 경쟁으로 만들지 않는다)
 *            로드맵 3.5절(라운드 안의 판돈 - 걸기) · 4절 1번(Marvel Snap의 스냅) · M7 "고정할 규칙" 1~5
 *
 * ## 이 파일이 존재하는 이유 - 판을 키우는 단추 하나
 *
 * 수색 중 한 번, 관찰에 확신이 들 때 사람이 스스로 판을 키운다. 선언 뒤의 발견은 점수가 ×2가 되고
 * 빗나감은 탄환을 두 배로 먹는다. **속도를 보상하지 않는다** - 빗맞는 값이 오르므로 사격이 오히려 더 신중해지고,
 * 그것이 관찰 게임의 방향과 같다. 실패해도 이미 얻은 점수는 깎이지 않는다(`hunt.ts` "점수판의 숫자는 위로만").
 *
 * 찾는 사람의 걸기(`canRaise` · `RAISE_MULTIPLIER`)와 다녀간 사람의 걸기(`normalizeWager` · `wagerMultiplier` -
 * "N개는 못 찾는다")가 한 파일에 있는 이유는 둘이 **같은 배수**를 쓰기 때문이다. 배수가 두 파일에 있으면
 * 한쪽만 3이 되는 날이 오고, 그날 결과 화면의 두 줄이 다른 규칙으로 말한다.
 *
 * ## 절대 규칙 - 시간 조건이 없다
 *
 * 걸기의 조건은 **잔탄**이지 시간이 아니다. "남은 시간 N초 안에만" 같은 조건은 종반 올인을 만들고, 그 순간
 * 걸기는 관찰의 확신이 아니라 시계를 보는 행위가 된다(`combo.ts` 머리말과 같은 이유 - 이 게임은 관찰 게임이다).
 * 그래서 이 파일의 함수 시그니처에는 `ms`가 하나도 없다. `raise.test.ts`가 소스를 스캔해 그 사실을 지킨다 -
 * 나중에 누가 타이머를 붙이려면 이 파일의 API를 먼저 바꿔야 하고, 그때 이 주석을 읽게 된다.
 *
 * ## 순수 함수뿐이다
 *
 * 상태를 받지 않고 숫자·불리언·작은 값 객체만 받는다. 싱글 리듀서(`gameState.ts`)와 매치 리듀서(`match/matchShoot.ts` ·
 * `match/matchChange.ts` · `match/matchScore.ts`)가 **같은 함수**를 부르고, `matchParity.test.ts`가 두 리듀서의 열이 같음을
 * 지킨다. 어느 리듀서가 여기 비교식을 한 줄 베껴 적는 순간 거기서 터진다. `core`·`game` 리프(`tuning` 타입)만 본다.
 */

import type { Tuning } from './tuning'

/* --- 배수 ------------------------------------------------------------------------ */

/**
 * 걸기의 배수. 찾는 사람의 선언 뒤 발견 점수와, 다녀간 사람의 맞은 걸기 점수가 **같은 값**으로 곱해진다.
 *
 * 2인 이유: 3 이상이면 걸기 한 번이 콤보 상한(3.0)과 맞먹어 "관찰보다 선언"이 전략이 되고, 1.5는 빗나감 2발의
 * 대가에 비해 보상이 작아 아무도 걸지 않는다. 튜닝 파일에 두지 않은 것은 배점(`hunt.ts`의 `POINTS_PER_FIND`)과 같은
 * 이유다 - 라운드 규칙 수치가 아니라 점수 규칙이고, 바꾸려면 두 걸기를 함께 봐야 한다.
 */
export const RAISE_MULTIPLIER = 2

/** 걸지 않았거나 걸기가 틀렸을 때의 배수. 벌은 없다 - 배수만 없다(로드맵 3.5절 · 규칙 5). */
const NO_RAISE_MULTIPLIER = 1

/* --- 찾는 사람의 걸기 ---------------------------------------------------------------- */

/**
 * 지금 걸 수 있는가. 조건은 둘이고 둘 다 시간이 아니다.
 *
 *  - **잔탄이 `raiseMinAmmo` 이상.** 3 이하에서는 "남은 것을 전부 얹는" 절박한 올인이라 거부한다(로드맵 M7 규칙 3).
 *    걸기는 관찰에 확신이 들 때 판을 키우는 것이지 이미 진 판을 뒤집는 주사위가 아니다.
 *  - **아직 안 걸었다.** 라운드당 한 번이다. 두 번을 허용하면 배수가 4·8로 쌓여 콤보의 자리를 대신하고,
 *    관찰 게임의 점수가 선언 횟수의 게임이 된다.
 *
 * 페이즈·역할·관전 게이트는 여기 없다 - 그건 리듀서의 것이다(`hunt.ts` 머리말 "게이트와 판정을 한 타입에 섞지 않는다").
 * 이 함수는 두 리듀서가 좌석의 두 필드를 그대로 넘겨 **같은 답**을 받는 자리다. 시그니처에 `ms`가 없는 것이 규칙의 증명이다.
 */
export function canRaise(ammo: number, raised: boolean, tuning: Tuning): boolean {
  if (raised) return false
  return ammo >= tuning.raiseMinAmmo
}

/**
 * 발견 한 건에 곱할 걸기 배수. `hunt.findPointsForEntry`가 콤보 배수 위에 이것을 곱한다 -
 * 자막의 "+240"과 결과 내역의 합계가 **같은 함수**에서 나와야 하므로 배수 자체는 여기 한 벌만 있다.
 */
export function raiseMultiplier(raised: boolean): number {
  return raised ? RAISE_MULTIPLIER : NO_RAISE_MULTIPLIER
}

/* --- 다녀간 사람의 걸기 --------------------------------------------------------------- */

/** 다녀간 사람이 커밋 때 거는 것 - "N개는 못 찾는다". `match/matchTypes.ChangerWager`와 같은 모양이다. */
export type WagerLike = Readonly<{ unfoundAtLeast: number }>

/**
 * 와이어에서 온 걸기를 규칙에 맞는 값으로 접는다. 아니면 `null`(안 건 것).
 *
 * 걸기 자체는 선택이라 잘못된 값으로 커밋을 통째로 거절하지 않는다 - 60초 동안 만든 것이 걸기 값 하나로 버려지면 안 된다.
 * 대신 걸기만 없는 것으로 본다. 거부하는 값이 셋이고 각각 다른 것을 막는다:
 *  - **정수가 아니거나 유한하지 않다**(`NaN` · `2.5` · 문자열이 흘러든 경우). 와이어 입력이라 모양을 믿을 수 없다.
 *  - **1 미만.** "0개는 못 찾는다"는 언제나 맞아 공짜 ×2가 된다.
 *  - **변화 개수 초과.** "13개는 못 찾는다"는 12개 방에서 언제나 틀려 뜻이 없다 - 조용히 잘라 12로 읽으면
 *    사람이 건 값과 화면의 값이 다르다. 그런 값은 건 것이 아니다.
 */
export function normalizeWager(wager: WagerLike | null | undefined, changeCount: number): WagerLike | null {
  // `null`만 걸러 `undefined`를 역참조하면 `wager` 키가 빠진 와이어 메시지(`protocol.isPlayerEventShape`는 commitChanges 본문을
  // 검사하지 않는다)가 호스트에서 TypeError를 던진다. 객체가 아닌 것은 전부 "걸지 않은 것"이다 - 서버가 그대로 import 하는 파일이다.
  if (typeof wager !== 'object' || wager === null) return null
  const atLeast: unknown = wager.unfoundAtLeast
  if (typeof atLeast !== 'number' || !Number.isSafeInteger(atLeast)) return null
  if (atLeast < 1 || atLeast > changeCount) return null
  return { unfoundAtLeast: atLeast }
}

/**
 * 걸기가 맞았는가 - 끝내 못 찾힌 것이 건 개수 이상이다. 걸지 않았으면 거짓이다(맞은 것이 아니라 건 것이 없다).
 * 결과 화면이 "맞았다/틀렸다" 도장을 찍을 때와 점수가 배수를 정할 때 **같은 비교**를 써야 하므로 함수 하나로 둔다.
 */
export function isWagerMet(unfoundCount: number, wager: WagerLike | null): boolean {
  return wager !== null && unfoundCount >= wager.unfoundAtLeast
}

/**
 * 다녀간 사람의 점수에 곱할 배수. 맞으면 ×2, 틀리거나 안 걸었으면 ×1 - **벌은 없다**(로드맵 M7 규칙 5).
 * 틀린 걸기에서 점수를 깎으면 "숨긴 것이 안 들키면 득점"(컨셉시트 4.5절)이 "들키면 감점"으로 뒤집히고,
 * 그 순간 다녀간 사람의 라운드는 웃긴 실패가 아니라 억울한 실패가 된다.
 */
export function wagerMultiplier(unfoundCount: number, wager: WagerLike | null): number {
  return isWagerMet(unfoundCount, wager) ? RAISE_MULTIPLIER : NO_RAISE_MULTIPLIER
}
